import React, { useEffect, useState } from 'react';

import { useSelector, useDispatch } from 'react-redux';
import moment from 'moment';
import { deleteUserReview, fetchUserReviews } from '../../../actions/userActions';
import { useProfileContentScroll } from '../../../hooks/useProfileContentScroll';
import { TabViewContainer } from '../../commonComponents/TabViewContainer';
import removeIcon from '../../../images/close-cross.png';
import CustomPortal from '../../commonComponents/CustomPortal';
import WarningAlertIcon from '../../../images/warning_alert_outline.svg';
import Styles from './UserReview.module.scss';

const LIMIT_VALUE = 15;

/**
 * The monolith Reviews table, on `admin/users/{id}/reviews`. That endpoint
 * queries `{ rating_to: user }` for any user, so one component serves both
 * profile types — which is why it is the flag-off fallback for offices as well
 * as the permanent professional view.
 *
 * Two callers, and the difference between them is `isOffice`:
 *
 * - **Professional profiles** (`Profile.js:865`) render it unconditionally.
 *   This is the only source of office→professional reviews, so nothing in
 *   GOTU-6266 moves it.
 * - **Office profiles** (`Profile.js:948`, via `OfficeReviewsTabScreen`) render
 *   it only while the GOTU-6266 flag is off, in place of `OfficeReviews`.
 *   organization-service has not shipped, so this is what staging and
 *   production serve.
 *
 * `isOffice` restores the three column branches GOTU-7338 removed when it
 * pointed offices at `OfficeReviews` and left this file professional-only. They
 * are back rather than duplicated into a legacy component: the office and
 * professional tables differ by three conditional column groups and one cell,
 * and two copies of a 180-line table would drift for as long as the gate lives.
 * Delete the prop and its branches when the flag is retired.
 *
 * Left on the Redux path deliberately. This table has no loading or error state
 * and adding one would change how the page looks. The `isFinalPage` bug it
 * inherits (`userReducer.js:265` compares a page's length against `limit`, so a
 * short-but-not-final page ends the scroll early) is likewise left for the
 * professional revamp.
 */
export default ({ goToJobProfile, isOffice = false }) => {
  const dispatch = useDispatch();

  const { reviews, reviewsPagination, isFinalPage, isActiveRequest } = useSelector(
    (state) => state.users.reviews,
  );

  const [selectedReview, setSelectedReview] = useState(null);

  useEffect(() => {
    dispatch(fetchUserReviews({ page: 1, limit: LIMIT_VALUE }));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useProfileContentScroll(
    // eslint-disable-next-line no-unsafe-optional-chaining
    () => dispatch(fetchUserReviews({ page: reviewsPagination?.page + 1, limit: LIMIT_VALUE })),
    { enabled: !isActiveRequest && !isFinalPage },
  );

  const formatAverageRating = (rating) => {
    if (rating) {
      return Number(rating).toFixed(1);
    }

    return rating;
  };

  const openJobProfile = (review) => goToJobProfile(review.joboffer_id);

  const renderReview = (review) => (
    <tr key={review.id} className={Styles.row}>
      <td
        className={`card-text ${Styles.jobCell}`}
        onClick={() => openJobProfile(review)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            openJobProfile(review);
          }
        }}
        role="button"
        tabIndex={0}
      >
        View Job
      </td>
      <td className="card-text table-value-text">
        {moment(review.joboffer_id?.job_datetime).format('ddd, MMM DD, YYYY')}
      </td>
      <td className="card-text table-value-text">
        {moment(review.createdAt).format('ddd, MMM DD, YYYY')}
      </td>

      {review.rating_by?.user_type === 'HYG' ? (
        <td className="card-text table-value-text">
          {review.rating_by?.first_name} {review.rating_by?.last_name || 'N/A'}
        </td>
      ) : (
        <td className="card-text table-value-text">{review.rating_by?.office_name || 'N/A'}</td>
      )}

      <td className="card-text table-value-text">{review.rating_count || 'N/A'}</td>

      {/* The monolith only scores these three for office→professional reviews. */}
      {!isOffice && (
        <>
          <td className="card-text table-value-text">
            {formatAverageRating(review.professionalSkills) || 'N/A'}
          </td>

          <td className="card-text table-value-text">
            {formatAverageRating(review.interpersonalSkills) || 'N/A'}
          </td>

          <td className="card-text table-value-text">
            {formatAverageRating(review.timeManagement) || 'N/A'}
          </td>
        </>
      )}

      <td className="card-text table-value-text">
        {formatAverageRating(review.averageRating) || 'N/A'}
      </td>

      <td className="card-text table-value-text">
        {' '}
        {isOffice ? 'N/A' : review.like_to_hire_again}{' '}
      </td>
      <td className="card-text table-value-text"> {review.feedback_for_tempmee} </td>
      <td className="card-text table-value-text">
        {review.isRemovable ? (
          // The role goes on a wrapping span, not on the `<img>` — an image is
          // a non-interactive element and cannot take `role="button"`. A native
          // `<button>` is banned by the design-system ratchet, and the DS
          // `IconButton` would change how this cell looks, which GOTU-7338 must
          // not do on the professional page. A span carrying the role keeps the
          // appearance byte-identical while making a destructive control
          // keyboard-reachable, which it was not before. Its accessible name
          // comes from the image's `alt`.
          <span
            role="button"
            tabIndex={0}
            onClick={() => setSelectedReview(review)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                setSelectedReview(review);
              }
            }}
          >
            <img src={removeIcon} className={Styles.removeIcon} alt="remove_icon" />
          </span>
        ) : null}
      </td>
    </tr>
  );

  const renderTitle = () => (
    <tr className={`${Styles.row} ${Styles.headerRow}`}>
      <td className="table-col-10 ard-text table-title-text">Shift</td>
      <td className="table-col-10 card-text table-title-text">Shift Date</td>
      <td className="table-col-10 card-text table-title-text">Created Date</td>

      <td className="table-col-15 card-text table-title-text">Name</td>
      <td className="card-text table-title-text">Review (Old)</td>
      {!isOffice && (
        <>
          <td className="card-text table-title-text">Professional Skills</td>
          <td className="card-text table-title-text">Interpersonal Skills</td>
          <td className="card-text table-title-text">Time Management</td>
        </>
      )}
      <td className="card-text table-title-text">AVG Rating</td>
      <td className="card-text table-title-text">Will Hire Again</td>
      <td className="table-col-10 card-text table-title-text">Feedback</td>
      <td className="card-text table-title-text">Remove</td>
    </tr>
  );

  return (
    <>
      <TabViewContainer title="Reviews">
        {reviews.length !== 0 ? (
          <div className={Styles.tableScroll}>
            <table>
              {renderTitle()}
              {reviews.map((review) => renderReview(review))}
            </table>
          </div>
        ) : (
          'There are no reviews'
        )}
      </TabViewContainer>

      {selectedReview && (
        <CustomPortal
          Icon={WarningAlertIcon}
          title="Please Confirm"
          text={
            <>
              Do you want to remove the rating for the shift on
              <br />
              {`\n${moment(selectedReview?.joboffer_id?.job_datetime).format('ddd, MMM DD, YYYY')}?`}
            </>
          }
          textStyle={{ marginTop: 5, marginBottom: 20, textAlign: 'center' }}
          submitText="Confirm"
          cancelText="Go Back"
          submitPressed={() => {
            dispatch(deleteUserReview(selectedReview?.id));
            setSelectedReview(null);
          }}
          cancelPressed={() => {
            setSelectedReview(null);
          }}
        />
      )}
    </>
  );
};
